import { inject, Injectable } from '@angular/core';
import { Course } from '../../models/interfaces/course.interface';
import { coursesMock } from '../../mocks/interfaces/course.mock';
import { CoursePayload, CourseRelatedResources } from '../../types/payloads/course.payload';
import { Api } from '../api/api';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { CourseFilterParams } from '../../models/interfaces/course-filter-value.interface';
import { COURSE_DELETE_RETENTION_HOURS, HOUR_IN_MS } from '../../utils/variables';
import { CourseStatus } from '../../models/enums/course-status.enum';
import { categoriesMock } from '../../mocks/interfaces/category.mock';
import { Module } from '../../models/interfaces/module.interface';
import {
  Class,
  ClassPayload,
  Content,
  ContentPayload,
} from '../../models/interfaces/course-content.interface';
import {
  Evaluation,
  EvaluationOption,
  EvaluationOptionPayload,
  EvaluationPayload,
  EvaluationQuestion,
  EvaluationQuestionPayload,
} from '../../models/interfaces/course-evaluation.interface';
import { isFakeBackendEnabled } from '../../utils/fake-backend.util';
import { CourseListingMode } from '../../models/enums/course-listing-mode.enum';
import { CourseLocation } from '../../models/enums/course-location.enum';
import { CourseType } from '../../models/enums/course-type.enum';
import { durationHoursToMinutes, normalizeCourse, toCourseSearchPayload } from '../../utils/course-write.mapper';

@Injectable({
  providedIn: 'root',
})
export class CourseService {
  private readonly api = inject(Api);

  private readonly baseRoute = '/courses';

  private readonly joinTagsQuery = 'join=tags';

  public async getManyMocked(): Promise<Course[]> {
    this.purgeExpiredDeletedCourses();

    const sorted = this.applyListingModeFilter([...coursesMock]);
    sorted.sort((left, right) => left.title.localeCompare(right.title));
    return sorted;
  }

  public async searchMocked(params: CourseFilterParams): Promise<PaginationDto<Course>> {
    this.purgeExpiredDeletedCourses();

    let filtered = [...coursesMock];

    filtered = this.applyListingModeFilter(filtered, params.listingMode);

    if (params.status && params.status !== 'all') {
      filtered = filtered.filter((course) => course.status === params.status);
    }

    if (params.categories && params.categories.length > 0) {
      const categoryIds = new Set(params.categories.map((category) => category.id));
      filtered = filtered.filter((course) => course.category && categoryIds.has(course.category.id));
    }

    if (params.createdAtRange) {
      const [start, end] = params.createdAtRange;
      filtered = filtered.filter((course) => {
        const createdAt = new Date(course.createdAt);
        return (!start || createdAt >= start) && (!end || createdAt <= end);
      });
    }

    if (params.keyword) {
      const searchTerm = params.keyword.trim().toLowerCase();
      filtered = filtered.filter((course) => course.title.toLowerCase().includes(searchTerm) || course.description?.toLowerCase().includes(searchTerm));
    }

    const sorted = [...filtered];
    sorted.sort((left, right) => left.title.localeCompare(right.title));
    const pagedData = sorted.slice(((params.page || 1) - 1) * (params.limit || 10), (params.page || 1) * (params.limit || 10));

    return {
      data: pagedData,
      page: params.page || 1,
      pageCount: Math.max(1, Math.ceil(sorted.length / (params.limit || 10))),
      count: pagedData.length,
      total: sorted.length,
    };
  }

  public async getByIdMocked(courseId: string): Promise<Course> {
    const course = coursesMock.find((item) => item.id === courseId);

    if (!course) {
      throw new Error('Não encontrado.');
    }

    return course;
  }

  public async createMocked(payload: CoursePayload, related: CourseRelatedResources = this.emptyRelatedResources()): Promise<Course> {
    if (!payload.title.trim()) {
      throw new Error('Título do curso é obrigatório.');
    }

    const now = new Date();
    const courseId = `course-${crypto.randomUUID()}`;
    const modules = this.mapModules(related.modules, now, courseId);
    const contents = this.mapContents(related.contents, modules, now);

    const created: Course = {
      id: courseId,
      tenantId: 'tenant-uuid',
      title: payload.title.trim(),
      description: payload.description,
      shortDescription: payload.shortDescription?.trim() || undefined,
      durationMinutes: durationHoursToMinutes(related.durationHours),
      coverImageUrl: payload.coverImageUrl?.trim() || undefined,
      categoryId: payload.categoryId,
      category: categoriesMock.find((category) => category.id === payload.categoryId),
      tagsIds: related.tags.map((tag) => tag.id),
      tags: [...related.tags],
      groupIds: [],
      trackIds: [],
      type: payload.type ?? CourseType.COURSE,
      location: payload.location ?? CourseLocation.ONLINE,
      status: related.status,
      modules,
      contents,
      createdAt: now,
      updatedAt: now,
      isActive: related.status === CourseStatus.ACTIVE,
      deletedAt: null,
    };

    coursesMock.unshift(created);
    return created;
  }

  public async updateMocked(courseId: string, payload: CoursePayload, related: CourseRelatedResources = this.emptyRelatedResources()): Promise<Course> {
    const targetIndex = coursesMock.findIndex((course) => course.id === courseId);

    if (targetIndex < 0) {
      throw new Error('Curso não encontrado para edição.');
    }

    if (!payload.title.trim()) {
      throw new Error('Título do curso é obrigatório.');
    }

    const current = coursesMock[targetIndex];
    const now = new Date();
    const modules = this.mapModules(related.modules, now, current.id);
    const contents = this.mapContents(related.contents, modules, now);

    const updated: Course = {
      ...current,
      title: payload.title.trim(),
      description: payload.description,
      shortDescription: payload.shortDescription?.trim() || undefined,
      durationMinutes: durationHoursToMinutes(related.durationHours),
      coverImageUrl: payload.coverImageUrl?.trim() || undefined,
      categoryId: payload.categoryId || current.categoryId,
      category: categoriesMock.find((category) => category.id === payload.categoryId) || current.category,
      tagsIds: related.tags.map((tag) => tag.id),
      tags: [...related.tags],
      type: payload.type ?? current.type ?? CourseType.COURSE,
      location: payload.location ?? current.location ?? CourseLocation.ONLINE,
      status: related.status,
      modules,
      contents,
      updatedAt: now,
      isActive: related.status === CourseStatus.ACTIVE,
    };

    coursesMock[targetIndex] = updated;
    return updated;
  }

  public async softDeleteMocked(courseId: string): Promise<void> {
    const course = this.findCourseOrThrow(courseId);
    course.deletedAt = new Date();
    course.updatedAt = new Date();
  }

  public async permanentDeleteMocked(courseId: string): Promise<void> {
    const courseIndex = coursesMock.findIndex((item) => item.id === courseId);

    if (courseIndex < 0) {
      throw new Error('Curso não encontrado.');
    }

    coursesMock.splice(courseIndex, 1);
  }

  public async deactivateMocked(courseId: string): Promise<void> {
    const course = this.findCourseOrThrow(courseId);
    course.status = CourseStatus.INACTIVE;
    course.isActive = false;
    course.updatedAt = new Date();
  }

  public async restoreMocked(courseId: string): Promise<void> {
    const course = this.findCourseOrThrow(courseId);
    course.deletedAt = null;
    course.status = CourseStatus.ACTIVE;
    course.isActive = true;
    course.updatedAt = new Date();
  }

  // ─── Public API (fake backend ou integração real) ─────────────────────────

  public async getMany(): Promise<Course[]> {
    if (isFakeBackendEnabled()) {
      return this.getManyMocked();
    }

    const response = await this.api.getAsync<Course[]>(this.baseRoute);
    return (response ?? []).map((course) => normalizeCourse(course));
  }

  public async search(params: CourseFilterParams): Promise<PaginationDto<Course>> {
    if (isFakeBackendEnabled()) {
      return this.searchMocked(params);
    }

    const response = await this.api.postAsync<PaginationDto<Course>>(
      `${this.baseRoute}/search`,
      toCourseSearchPayload(params),
    );

    if (!response) {
      return {
        data: [],
        page: params.page || 1,
        pageCount: 0,
        count: 0,
        total: 0,
      };
    }

    return {
      ...response,
      data: (response.data ?? []).map((course) => normalizeCourse(course)),
    };
  }

  public async getById(courseId: string): Promise<Course | null> {
    if (isFakeBackendEnabled()) {
      return this.getByIdMocked(courseId);
    }

    const response = await this.api.getAsync<Course>(`${this.baseRoute}/${courseId}?${this.joinTagsQuery}`);
    return response ? normalizeCourse(response) : null;
  }

  public async softDelete(courseId: string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.softDeleteMocked(courseId);
    }

    await this.api.deleteAsync(`${this.baseRoute}/soft-delete/${courseId}`);
  }

  public async permanentDelete(courseId: string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.permanentDeleteMocked(courseId);
    }

    await this.api.deleteAsync(`${this.baseRoute}/${courseId}/permanent`);
  }

  public async deactivate(courseId: string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.deactivateMocked(courseId);
    }

    await this.api.patchAsync(`${this.baseRoute}/${courseId}/status`, { status: false });
  }

  public async restore(courseId: string): Promise<void> {
    if (isFakeBackendEnabled()) {
      return this.restoreMocked(courseId);
    }

    await this.api.patchAsync(`${this.baseRoute}/restore/${courseId}`);
  }

  public async create(payload: CoursePayload, related?: CourseRelatedResources): Promise<Course> {
    if (isFakeBackendEnabled()) {
      return this.createMocked(payload, related);
    }

    const newCourse = await this.api.postAsync<Course>(`${this.baseRoute}?${this.joinTagsQuery}`, payload);

    if (!newCourse) {
      throw new Error('Falha ao criar o curso.');
    }

    return normalizeCourse(newCourse);
  }

  public async update(courseId: string, payload: CoursePayload, related?: CourseRelatedResources): Promise<Course> {
    if (isFakeBackendEnabled()) {
      return this.updateMocked(courseId, payload, related);
    }

    const updatedCourse = await this.api.putAsync<Course>(`${this.baseRoute}/${courseId}?${this.joinTagsQuery}`, payload);

    if (!updatedCourse) {
      throw new Error('Falha ao atualizar o curso.');
    }

    return normalizeCourse(updatedCourse);
  }

  private emptyRelatedResources(): CourseRelatedResources {
    return {
      tags: [],
      status: CourseStatus.DRAFT,
      modules: [],
      contents: [],
    };
  }

  private mapModules(modules: CourseRelatedResources['modules'], now: Date, courseId: string): Module[] {
    return modules.map((module, index) => ({
      id: `module-${crypto.randomUUID()}`,
      createdAt: now,
      updatedAt: now,
      order: module.order ?? index,
      title: module.title,
      courseId,
      duration: 0,
      classesIds: [],
      isActive: module.isActive !== false,
    }));
  }

  private mapContents(contents: CourseRelatedResources['contents'], modules: Module[], now: Date): Content[] {
    return contents.map((content, index) => this.mapContent(content, modules, index, now));
  }

  private mapContent(
    content: ContentPayload,
    modules: Module[],
    index: number,
    now: Date,
  ): Content {
    const moduleId =
      modules.find((module) => module.order === content.moduleOrder)?.id
      ?? modules[index]?.id
      ?? `module-${crypto.randomUUID()}`;

    return {
      id: `content-${crypto.randomUUID()}`,
      createdAt: now,
      updatedAt: now,
      isActive: true,
      moduleId,
      moduleOrder: content.moduleOrder,
      classes: content.classes.map((classItem, order) => this.mapClass(classItem, order, now)),
      evaluations: content.evaluations.map((evaluation, order) =>
        this.mapEvaluation(evaluation, order, now),
      ),
    };
  }

  private mapClass(classItem: ClassPayload, order: number, now: Date): Class {
    return {
      id: `class-${crypto.randomUUID()}`,
      createdAt: now,
      updatedAt: now,
      isActive: true,
      order: classItem.order ?? order,
      title: classItem.title,
      type: classItem.type,
      videoTitle: classItem.videoTitle,
      videoThumbnailUrl: classItem.videoThumbnailUrl,
      videoFileName: classItem.videoFileName,
      editorContent: classItem.editorContent,
      youtubeUrl: classItem.youtubeUrl,
      vimeoUrl: classItem.vimeoUrl,
      webPageFileName: classItem.webPageFileName,
    };
  }

  private mapEvaluation(evaluation: EvaluationPayload, order: number, now: Date): Evaluation {
    return {
      id: `evaluation-${crypto.randomUUID()}`,
      createdAt: now,
      updatedAt: now,
      isActive: true,
      order: evaluation.order ?? order,
      questions: evaluation.questions.map((question, questionOrder) =>
        this.mapQuestion(question, questionOrder, now),
      ),
    };
  }

  private mapQuestion(
    question: EvaluationQuestionPayload,
    questionOrder: number,
    now: Date,
  ): EvaluationQuestion {
    return {
      id: `question-${crypto.randomUUID()}`,
      createdAt: now,
      updatedAt: now,
      isActive: true,
      order: question.order ?? questionOrder,
      text: question.text,
      imageUrl: question.imageUrl,
      options: question.options.map((option, optionOrder) =>
        this.mapOption(option, optionOrder, now),
      ),
    };
  }

  private mapOption(
    option: EvaluationOptionPayload,
    optionOrder: number,
    now: Date,
  ): EvaluationOption {
    return {
      id: `option-${crypto.randomUUID()}`,
      createdAt: now,
      updatedAt: now,
      isActive: true,
      order: option.order ?? optionOrder,
      text: option.text,
      isCorrect: option.isCorrect,
    };
  }

  private findCourseOrThrow(courseId: string): Course {
    const course = coursesMock.find((item) => item.id === courseId);

    if (!course) {
      throw new Error('Curso não encontrado.');
    }

    return course;
  }

  private applyListingModeFilter(courses: Course[], listingMode?: CourseListingMode): Course[] {
    if (listingMode === CourseListingMode.TRASH) {
      return courses.filter((course) => !!course.deletedAt);
    }

    if (listingMode === CourseListingMode.DRAFTS) {
      return courses.filter((course) => !course.deletedAt && course.status === CourseStatus.DRAFT);
    }

    if (listingMode === CourseListingMode.PUBLISHED) {
      return courses.filter((course) => !course.deletedAt && course.status !== CourseStatus.DRAFT);
    }

    return courses.filter((course) => !course.deletedAt);
  }

  private purgeExpiredDeletedCourses(): void {
    const expiredCourseIds = coursesMock
      .filter((course) => course.deletedAt && this.isCoursePermanentlyExpired(course.deletedAt))
      .map((course) => course.id);

    expiredCourseIds.forEach((courseId) => {
      const courseIndex = coursesMock.findIndex((item) => item.id === courseId);

      if (courseIndex >= 0) {
        coursesMock.splice(courseIndex, 1);
      }
    });
  }

  private isCoursePermanentlyExpired(deletedAt: Date, referenceDate = new Date()): boolean {
    const retentionMs = COURSE_DELETE_RETENTION_HOURS * HOUR_IN_MS;
    return referenceDate.getTime() - deletedAt.getTime() >= retentionMs;
  }
}
